import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Calculator,
  ChevronRight,
  ExternalLink,
  BookOpen,
  FileText,
  DollarSign,
  TrendingUp,
  TrendingDown,
  RefreshCw,
  Loader2,
  Building2,
  CreditCard,
  Wallet,
  PiggyBank,
  Receipt,
  Plus,
  BarChart3,
  Link2,
  CalendarDays,
  Settings2,
  Scale,
  CalendarCheck,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useSessionState } from '@/hooks/use-session-state';
import { useTabState } from '@/hooks/use-tab-state';
import { trpc } from '@/lib/trpc';
import { formatCurrency } from '@/lib/utils';
import { ChartWorkbench } from '@/components/travel/accounting/chart-workbench';

type TabType = 'overview' | 'accounts' | 'journal' | 'rates';
const VALID_TABS: readonly TabType[] = ['overview', 'accounts', 'journal', 'rates'];

export function AccountingPage() {
  const { t } = useTranslation('accounting');
  // Active tab lives in the `tab` URL query param (Back from a detail page
  // restores it) and is mirrored to sessionStorage so it also survives
  // navigating away via the sidebar and returning to the bare path.
  const [activeTab, setActiveTab] = useTabState<TabType>(
    'travel:accounting:activeTab',
    VALID_TABS,
    'overview'
  );

  const { data: accountStats, isLoading: isLoadingStats } = trpc.account.stats.useQuery();
  const { data: journalStats, isLoading: isLoadingJournal } = trpc.journalEntry.stats.useQuery();
  const { data: accountTree, isLoading: isLoadingTree } = trpc.account.getTree.useQuery({
    isActive: true,
  });

  const isLoading = isLoadingStats || isLoadingJournal || isLoadingTree;

  const utils = trpc.useUtils();

  const fetchTCMBMutation = trpc.exchangeRate.fetchFromKKTCMB.useMutation({
    onSuccess: () => {
      utils.exchangeRate.list.invalidate();
    },
  });

  // Recursively sum balances for an account and all its children
  const sumTree = (node: { balance: number; children: typeof accountTree }): number => {
    const childrenSum = (node.children ?? []).reduce((s, c) => s + sumTree(c), 0);
    return Number(node.balance) + childrenSum;
  };

  const calculateTypeTotal = (type: string) => {
    return accountTree?.filter((a) => a.type === type).reduce((sum, a) => sum + sumTree(a), 0) || 0;
  };

  const totalAssets = calculateTypeTotal('ASSET');
  const totalLiabilities = Math.abs(calculateTypeTotal('LIABILITY'));
  const totalEquity = Math.abs(calculateTypeTotal('EQUITY'));
  const totalIncome = Math.abs(calculateTypeTotal('INCOME'));
  const totalExpenses = Math.abs(calculateTypeTotal('EXPENSE'));
  const netIncome = totalIncome - totalExpenses;

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">{t('title')}</h1>
          <p className="text-muted-foreground">{t('subtitle')}</p>
        </div>
        <div className="flex items-center gap-2">
          <Link to="/travel/accounting/posting-rules">
            <Button variant="outline" className="h-8">
              <Settings2 className="mr-2 h-4 w-4" />
              {t('postingRules', 'Entegrasyon Tanımları')}
            </Button>
          </Link>
          <Link to="/travel/accounting/tie-out">
            <Button variant="outline" className="h-8">
              <Scale className="mr-2 h-4 w-4" />
              {t('tieOut', 'Mutabakat')}
            </Button>
          </Link>
          <Link to="/travel/accounting/period-end">
            <Button variant="outline" className="h-8">
              <CalendarCheck className="mr-2 h-4 w-4" />
              {t('periodEnd', 'Dönem Sonu')}
            </Button>
          </Link>
          <Link to="/travel/accounting/reports">
            <Button variant="outline" className="h-8">
              <BarChart3 className="mr-2 h-4 w-4" />
              {t('financialReports')}
            </Button>
          </Link>
          <Button
            variant="outline"
            className="h-8"
            onClick={() => fetchTCMBMutation.mutate()}
            disabled={fetchTCMBMutation.isPending}
          >
            {fetchTCMBMutation.isPending ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <RefreshCw className="mr-2 h-4 w-4" />
            )}
            {t('fetchTCMBRates')}
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b">
        <nav className="flex gap-4">
          {[
            { id: 'overview', labelKey: 'tabs.overview', icon: Calculator },
            { id: 'accounts', labelKey: 'tabs.accounts', icon: BookOpen },
            { id: 'journal', labelKey: 'tabs.journal', icon: FileText },
            { id: 'rates', labelKey: 'tabs.rates', icon: DollarSign },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as TabType)}
              className={`flex items-center gap-2 border-b-2 px-4 py-3 text-sm font-medium transition-colors ${
                activeTab === tab.id
                  ? 'border-primary text-primary'
                  : 'text-muted-foreground hover:text-foreground border-transparent'
              }`}
            >
              <tab.icon className="h-4 w-4" />
              {t(tab.labelKey)}
            </button>
          ))}
        </nav>
      </div>

      {/* Loading State */}
      {isLoading && (
        <div className="space-y-6">
          <div className="grid gap-4 md:grid-cols-3 lg:grid-cols-6">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="border-border bg-card border p-4">
                <div className="flex items-center gap-3">
                  <div className="bg-muted h-9 w-9 animate-pulse" />
                  <div className="flex-1 space-y-2">
                    <div className="bg-muted h-5 w-24 animate-pulse" />
                    <div className="bg-muted h-3 w-16 animate-pulse" />
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div className="grid gap-6 lg:grid-cols-2">
            {Array.from({ length: 2 }).map((_, i) => (
              <div key={i} className="border-border bg-card border p-6">
                <div className="bg-muted mb-4 h-5 w-32 animate-pulse" />
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <div className="bg-muted h-8 w-20 animate-pulse" />
                    <div className="bg-muted h-8 w-20 animate-pulse" />
                  </div>
                  {Array.from({ length: 5 }).map((_, j) => (
                    <div key={j} className="flex justify-between">
                      <div className="bg-muted h-4 w-24 animate-pulse" />
                      <div className="bg-muted h-4 w-12 animate-pulse" />
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Overview Tab */}
      {!isLoading && activeTab === 'overview' && (
        <div className="space-y-6">
          {/* Financial Summary Cards */}
          <div className="grid gap-4 md:grid-cols-3 lg:grid-cols-6">
            <div className="border-border bg-card border p-4">
              <div className="flex items-center gap-3">
                <div className="bg-blue-100 p-2">
                  <Wallet className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-lg font-bold">{formatCurrency(totalAssets, 'TRY')}</p>
                  <p className="text-muted-foreground text-sm">{t('summary.totalAssets')}</p>
                </div>
              </div>
            </div>
            <div className="border-border bg-card border p-4">
              <div className="flex items-center gap-3">
                <div className="bg-red-100 p-2">
                  <CreditCard className="h-5 w-5 text-red-600" />
                </div>
                <div>
                  <p className="text-lg font-bold">{formatCurrency(totalLiabilities, 'TRY')}</p>
                  <p className="text-muted-foreground text-sm">{t('summary.liabilities')}</p>
                </div>
              </div>
            </div>
            <div className="border-border bg-card border p-4">
              <div className="flex items-center gap-3">
                <div className="bg-purple-100 p-2">
                  <Building2 className="h-5 w-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-lg font-bold">{formatCurrency(totalEquity, 'TRY')}</p>
                  <p className="text-muted-foreground text-sm">{t('summary.equity')}</p>
                </div>
              </div>
            </div>
            <div className="border-border bg-card border p-4">
              <div className="flex items-center gap-3">
                <div className="bg-green-100 p-2">
                  <TrendingUp className="h-5 w-5 text-green-600" />
                </div>
                <div>
                  <p className="text-lg font-bold">{formatCurrency(totalIncome, 'TRY')}</p>
                  <p className="text-muted-foreground text-sm">{t('summary.income')}</p>
                </div>
              </div>
            </div>
            <div className="border-border bg-card border p-4">
              <div className="flex items-center gap-3">
                <div className="bg-orange-100 p-2">
                  <TrendingDown className="h-5 w-5 text-orange-600" />
                </div>
                <div>
                  <p className="text-lg font-bold">{formatCurrency(totalExpenses, 'TRY')}</p>
                  <p className="text-muted-foreground text-sm">{t('summary.expenses')}</p>
                </div>
              </div>
            </div>
            <div className="border-border bg-card border p-4">
              <div className="flex items-center gap-3">
                <div className={`p-2 ${netIncome >= 0 ? 'bg-green-100' : 'bg-red-100'}`}>
                  <PiggyBank
                    className={`h-5 w-5 ${netIncome >= 0 ? 'text-green-600' : 'text-red-600'}`}
                  />
                </div>
                <div>
                  <p className="text-lg font-bold">{formatCurrency(netIncome, 'TRY')}</p>
                  <p className="text-muted-foreground text-sm">{t('summary.netIncome')}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Stats Row */}
          <div className="grid gap-6 md:grid-cols-2">
            {/* Account Stats */}
            {accountStats && (
              <div className="border-border bg-card border p-6">
                <h3 className="mb-4 text-lg font-semibold">{t('chartOfAccounts.title')}</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-muted/50 p-3">
                    <p className="text-2xl font-bold">{accountStats.total}</p>
                    <p className="text-muted-foreground text-sm">
                      {t('chartOfAccounts.totalAccounts')}
                    </p>
                  </div>
                  <div className="bg-muted/50 p-3">
                    <p className="text-2xl font-bold">{accountStats.active}</p>
                    <p className="text-muted-foreground text-sm">{t('chartOfAccounts.active')}</p>
                  </div>
                </div>
                <div className="mt-4 space-y-2">
                  <div className="flex justify-between text-sm">
                    <span>{t('chartOfAccounts.assets')}</span>
                    <span className="font-medium">{accountStats.byType.ASSET}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span>{t('chartOfAccounts.liabilities')}</span>
                    <span className="font-medium">{accountStats.byType.LIABILITY}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span>{t('chartOfAccounts.equity')}</span>
                    <span className="font-medium">{accountStats.byType.EQUITY}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span>{t('chartOfAccounts.income')}</span>
                    <span className="font-medium">{accountStats.byType.INCOME}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span>{t('chartOfAccounts.expenses')}</span>
                    <span className="font-medium">{accountStats.byType.EXPENSE}</span>
                  </div>
                </div>
                <Button
                  variant="outline"
                  className="mt-4 h-8 w-full"
                  onClick={() => setActiveTab('accounts')}
                >
                  {t('chartOfAccounts.viewAll')}
                  <ChevronRight className="ml-2 h-4 w-4" />
                </Button>
              </div>
            )}

            {/* Journal Entry Stats */}
            {journalStats && (
              <div className="border-border bg-card border p-6">
                <h3 className="mb-4 text-lg font-semibold">{t('journalEntries.title')}</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-muted/50 p-3">
                    <p className="text-2xl font-bold">{journalStats.total}</p>
                    <p className="text-muted-foreground text-sm">
                      {t('journalEntries.totalEntries')}
                    </p>
                  </div>
                  <div className="bg-muted/50 p-3">
                    <p className="text-2xl font-bold">{journalStats.thisMonth}</p>
                    <p className="text-muted-foreground text-sm">{t('journalEntries.thisMonth')}</p>
                  </div>
                </div>
                <div className="mt-4 space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span className="flex items-center gap-2">
                      <span className="h-2 w-2 bg-gray-400" />
                      {t('journalEntries.draft')}
                    </span>
                    <span className="font-medium">{journalStats.draft}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="flex items-center gap-2">
                      <span className="h-2 w-2 bg-green-400" />
                      {t('journalEntries.posted')}
                    </span>
                    <span className="font-medium">{journalStats.posted}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="flex items-center gap-2">
                      <span className="h-2 w-2 bg-red-400" />
                      {t('journalEntries.voided')}
                    </span>
                    <span className="font-medium">{journalStats.voided}</span>
                  </div>
                </div>
                <Button
                  variant="outline"
                  className="mt-4 h-8 w-full"
                  onClick={() => setActiveTab('journal')}
                >
                  {t('journalEntries.viewAll')}
                  <ChevronRight className="ml-2 h-4 w-4" />
                </Button>
              </div>
            )}
          </div>

          {/* Quick Links */}
          <div className="border-border bg-card border p-6">
            <h3 className="mb-4 text-lg font-semibold">{t('quickActions.title')}</h3>
            <div className="grid gap-4 md:grid-cols-4 lg:grid-cols-7">
              <Link
                to="/travel/accounting/journal-entries/new"
                className="border-border hover:bg-muted/50 flex items-center gap-3 border p-4 transition-colors"
              >
                <FileText className="text-primary h-8 w-8" />
                <div>
                  <p className="font-medium">{t('quickActions.newJournalEntry')}</p>
                  <p className="text-muted-foreground text-sm">
                    {t('quickActions.recordTransaction')}
                  </p>
                </div>
              </Link>
              <Link
                to="/travel/accounting/reports"
                className="border-border hover:bg-muted/50 bg-primary/5 border-primary/20 flex items-center gap-3 border p-4 transition-colors"
              >
                <BarChart3 className="text-primary h-8 w-8" />
                <div>
                  <p className="font-medium">{t('quickActions.financialReports')}</p>
                  <p className="text-muted-foreground text-sm">{t('quickActions.allReports')}</p>
                </div>
              </Link>
              <Link
                to="/travel/accounting/reports?tab=trial-balance"
                className="border-border hover:bg-muted/50 flex items-center gap-3 border p-4 transition-colors"
              >
                <Receipt className="h-8 w-8 text-green-600" />
                <div>
                  <p className="font-medium">{t('quickActions.trialBalance')}</p>
                  <p className="text-muted-foreground text-sm">{t('quickActions.viewBalances')}</p>
                </div>
              </Link>
              <Link
                to="/travel/accounting/reports?tab=profit-loss"
                className="border-border hover:bg-muted/50 flex items-center gap-3 border p-4 transition-colors"
              >
                <TrendingUp className="h-8 w-8 text-blue-600" />
                <div>
                  <p className="font-medium">{t('quickActions.profitLoss')}</p>
                  <p className="text-muted-foreground text-sm">
                    {t('quickActions.incomeStatement')}
                  </p>
                </div>
              </Link>
              <Link
                to="/travel/accounting/reports?tab=balance-sheet"
                className="border-border hover:bg-muted/50 flex items-center gap-3 border p-4 transition-colors"
              >
                <Building2 className="h-8 w-8 text-purple-600" />
                <div>
                  <p className="font-medium">{t('quickActions.balanceSheet')}</p>
                  <p className="text-muted-foreground text-sm">
                    {t('quickActions.financialPosition')}
                  </p>
                </div>
              </Link>
              <Link
                to="/travel/accounting/reconciliation"
                className="border-border hover:bg-muted/50 flex items-center gap-3 border p-4 transition-colors"
              >
                <Link2 className="h-8 w-8 text-amber-600" />
                <div>
                  <p className="font-medium">{t('quickActions.bankReconciliation')}</p>
                  <p className="text-muted-foreground text-sm">
                    {t('quickActions.matchTransactions')}
                  </p>
                </div>
              </Link>
              <Link
                to="/travel/accounting/periods"
                className="border-border hover:bg-muted/50 flex items-center gap-3 border p-4 transition-colors"
              >
                <CalendarDays className="h-8 w-8 text-cyan-600" />
                <div>
                  <p className="font-medium">{t('quickActions.fiscalPeriods')}</p>
                  <p className="text-muted-foreground text-sm">{t('quickActions.managePeriods')}</p>
                </div>
              </Link>
            </div>
          </div>
        </div>
      )}

      {/* Chart of Accounts Tab */}
      {activeTab === 'accounts' && <AccountsTab />}

      {/* Journal Entries Tab */}
      {activeTab === 'journal' && <JournalEntriesTab />}

      {/* Exchange Rates Tab */}
      {activeTab === 'rates' && <ExchangeRatesTab />}
    </div>
  );
}

// Accounts Tab Component
// Tree node type matching the travel account API response

// Recursively filter tree nodes by search query
// The chart-of-accounts screen is a workbench now — inline editing, bulk
// actions, drag to re-parent, a health worklist and session undo all live in
// components/travel/accounting/chart-workbench.tsx. The old read-only tree and
// its filter helpers were removed with it.
function AccountsTab() {
  return <ChartWorkbench />;
}

// Journal Entries Tab Component
function JournalEntriesTab() {
  const { t, i18n } = useTranslation('accounting');
  const dateLocale = i18n.language === 'tr' ? 'tr-TR' : 'en-US';
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useSessionState<string>(
    'travel:accounting:statusFilter',
    ''
  );
  const limit = 15;

  const { data, isLoading } = trpc.journalEntry.list.useQuery({
    page,
    limit,
    status: (statusFilter as 'DRAFT' | 'POSTED' | 'VOIDED') || undefined,
  });

  const entries = data?.entries ?? [];
  const pagination = data?.pagination ?? { page: 1, limit: 15, total: 0, totalPages: 0 };

  const statusColors: Record<string, string> = {
    DRAFT: 'bg-gray-100 text-gray-700',
    PENDING_APPROVAL: 'bg-yellow-100 text-yellow-700',
    POSTED: 'bg-green-100 text-green-700',
    VOIDED: 'bg-red-100 text-red-700',
  };

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex items-center justify-between gap-4">
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="border-border bg-background focus:border-foreground focus:ring-foreground/20 h-8 border px-3 text-sm focus:outline-none focus:ring-1"
        >
          <option value="">{t('journalEntries.allStatuses')}</option>
          <option value="DRAFT">{t('journalEntries.draft')}</option>
          <option value="POSTED">{t('journalEntries.posted')}</option>
          <option value="VOIDED">{t('journalEntries.voided')}</option>
        </select>
        <Link to="/travel/accounting/journal-entries/new">
          <Button className="h-8">
            <Plus className="mr-2 h-4 w-4" />
            {t('journalEntries.newEntry')}
          </Button>
        </Link>
      </div>

      {/* Entries Table */}
      {isLoading ? (
        <div className="flex justify-center py-8">
          <Loader2 className="text-primary h-8 w-8 animate-spin" />
        </div>
      ) : entries.length === 0 ? (
        <div className="border-border bg-card border py-12 text-center">
          <FileText className="text-muted-foreground/50 mx-auto h-12 w-12" />
          <h3 className="mt-4 text-lg font-medium">{t('journalEntries.noEntriesFound')}</h3>
          <p className="text-muted-foreground mt-1 text-sm">
            {statusFilter ? t('journalEntries.adjustFilter') : t('journalEntries.getStarted')}
          </p>
          {!statusFilter && (
            <Link to="/travel/accounting/journal-entries/new">
              <Button className="mt-4 h-8">
                <Plus className="mr-2 h-4 w-4" />
                {t('journalEntries.createEntry')}
              </Button>
            </Link>
          )}
        </div>
      ) : (
        <div className="border-border bg-card overflow-hidden border">
          <table className="w-full">
            <thead className="bg-muted/50">
              <tr>
                <th className="px-4 py-2 text-left text-sm font-medium">
                  {t('journalEntries.entryNumber')}
                </th>
                <th className="px-4 py-2 text-left text-sm font-medium">
                  {t('journalEntries.date')}
                </th>
                <th className="px-4 py-2 text-left text-sm font-medium">
                  {t('journalEntries.description')}
                </th>
                <th className="px-4 py-2 text-left text-sm font-medium">
                  {t('journalEntries.reference')}
                </th>
                <th className="px-4 py-2 text-left text-sm font-medium">
                  {t('journalEntries.status')}
                </th>
                <th className="px-4 py-2 text-right text-sm font-medium">
                  {t('journalEntries.amount')}
                </th>
                <th className="w-12 px-4 py-2 text-center text-sm font-medium"></th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {entries.map((entry) => (
                <tr
                  key={entry.id}
                  className="hover:bg-muted/30 cursor-pointer"
                  onClick={() => navigate(`/travel/accounting/journal-entries/${entry.id}`)}
                >
                  <td className="px-4 py-2 font-mono text-sm">{entry.entryNumber}</td>
                  <td className="px-4 py-2 text-sm">
                    {new Date(entry.date).toLocaleDateString(dateLocale)}
                  </td>
                  <td className="px-4 py-2">
                    <p className="font-medium">{entry.description}</p>
                    <p className="text-muted-foreground text-xs">
                      {entry.lines.length} {t('journalEntries.lines')}
                    </p>
                  </td>
                  <td className="px-4 py-2 text-sm">
                    {entry.referenceType ? (
                      <span className="bg-muted px-2 py-0.5 text-xs">{entry.referenceType}</span>
                    ) : (
                      '-'
                    )}
                  </td>
                  <td className="px-4 py-2">
                    <span
                      className={`px-2 py-0.5 text-xs font-medium ${statusColors[entry.status]}`}
                    >
                      {entry.status}
                    </span>
                  </td>
                  <td className="px-4 py-2 text-right font-mono">
                    {formatCurrency(entry.totalDebit, entry.currency)}
                  </td>
                  <td className="px-4 py-2 text-center">
                    <ExternalLink className="text-muted-foreground h-4 w-4" />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      {pagination.totalPages > 1 && (
        <div className="flex justify-center gap-2">
          <Button
            variant="outline"
            size="sm"
            className="h-8"
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page <= 1}
          >
            {t('pagination.previous')}
          </Button>
          <span className="px-4 py-2 text-sm">
            {t('pagination.page', { current: page, total: pagination.totalPages })}
          </span>
          <Button
            variant="outline"
            size="sm"
            className="h-8"
            onClick={() => setPage((p) => Math.min(pagination.totalPages, p + 1))}
            disabled={page >= pagination.totalPages}
          >
            {t('pagination.next')}
          </Button>
        </div>
      )}
    </div>
  );
}

// Exchange Rates Tab Component
function ExchangeRatesTab() {
  const { t, i18n } = useTranslation('accounting');
  const dateLocale = i18n.language === 'tr' ? 'tr-TR' : 'en-US';
  const [page, setPage] = useState(1);
  const limit = 20;

  const { data, isLoading } = trpc.exchangeRate.list.useQuery({
    page,
    limit,
  });

  const utils = trpc.useUtils();

  const fetchTCMBMutation = trpc.exchangeRate.fetchFromKKTCMB.useMutation({
    onSuccess: () => {
      utils.exchangeRate.list.invalidate();
    },
  });

  const rates = data?.rates ?? [];
  const pagination = data?.pagination ?? { page: 1, limit: 20, total: 0, totalPages: 0 };

  return (
    <div className="space-y-4">
      {/* Actions */}
      <div className="flex justify-end">
        <Button
          variant="outline"
          className="h-8"
          onClick={() => fetchTCMBMutation.mutate()}
          disabled={fetchTCMBMutation.isPending}
        >
          {fetchTCMBMutation.isPending ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : (
            <RefreshCw className="mr-2 h-4 w-4" />
          )}
          {t('exchangeRates.fetchFromKKTCMB')}
        </Button>
      </div>

      {/* Rates Table */}
      {isLoading ? (
        <div className="flex justify-center py-8">
          <Loader2 className="text-primary h-8 w-8 animate-spin" />
        </div>
      ) : rates.length === 0 ? (
        <div className="border-border bg-card border py-12 text-center">
          <DollarSign className="text-muted-foreground/50 mx-auto h-12 w-12" />
          <h3 className="mt-4 text-lg font-medium">{t('exchangeRates.noRatesFound')}</h3>
          <p className="text-muted-foreground mt-1 text-sm">{t('exchangeRates.clickToLoad')}</p>
        </div>
      ) : (
        <div className="border-border bg-card overflow-hidden border">
          <table className="w-full">
            <thead className="bg-muted/50">
              <tr>
                <th className="px-4 py-2 text-left text-sm font-medium">
                  {t('exchangeRates.from')}
                </th>
                <th className="px-4 py-2 text-left text-sm font-medium">{t('exchangeRates.to')}</th>
                <th className="px-4 py-2 text-right text-sm font-medium">
                  {t('exchangeRates.rate')}
                </th>
                <th className="px-4 py-2 text-left text-sm font-medium">
                  {t('exchangeRates.date')}
                </th>
                <th className="px-4 py-2 text-left text-sm font-medium">
                  {t('exchangeRates.source')}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {rates.map((rate) => (
                <tr key={rate.id} className="hover:bg-muted/30">
                  <td className="px-4 py-2 font-mono text-sm">{rate.fromCurrency}</td>
                  <td className="px-4 py-2 font-mono text-sm">{rate.toCurrency}</td>
                  <td className="px-4 py-2 text-right font-mono">{rate.rate.toFixed(4)}</td>
                  <td className="px-4 py-2 text-sm">
                    {new Date(rate.date).toLocaleDateString(dateLocale)}
                  </td>
                  <td className="px-4 py-2">
                    <span className="bg-muted px-2 py-0.5 text-xs">{rate.source || 'MANUAL'}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      {pagination.totalPages > 1 && (
        <div className="flex justify-center gap-2">
          <Button
            variant="outline"
            size="sm"
            className="h-8"
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page <= 1}
          >
            {t('pagination.previous')}
          </Button>
          <span className="px-4 py-2 text-sm">
            {t('pagination.page', { current: page, total: pagination.totalPages })}
          </span>
          <Button
            variant="outline"
            size="sm"
            className="h-8"
            onClick={() => setPage((p) => Math.min(pagination.totalPages, p + 1))}
            disabled={page >= pagination.totalPages}
          >
            {t('pagination.next')}
          </Button>
        </div>
      )}
    </div>
  );
}
